import {
  Box,
  Button,
  Flex,
  Select,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { HorizontalScrollContainer } from '../../../components/horizontal-scroll-container/HorizontalScrollContainer';
import NoResourceFound from '../../../components/no-resource-found/NoResourceFound';
import ResourceGrid from '../../../components/resource-grid/ResourceGrid';
import ViewToggle from '../../../components/view-toggle/ViewToggle';
import { useBatchAbsences } from '../../../hooks/useBatchAbsences';
import { getPracticeById } from '../../../services/practices/getPracticeById';
import { getProvidersList } from '../../../services/providers/getProvidersList';
import { Practice, Provider } from '../../../types';
import { CustomField } from '../../../types/custom-fields/types';
import { isExternalPractice } from '../../../utils/externalEntity';
import { getProviderStatusRank } from '../../../utils/getProviderStatusRank';
import ProviderCompactCard from '../providers/ProviderCompactCard';
import { createProviderColumns } from '../providers/ProvidersColumns';

interface ProvidersUnderPracticeProps {
  providers: Provider[] | undefined;
  name: string;
  practiceId: string; // Add practiceId to pass down to ProviderCompactCard
  customFields?: CustomField[]; // Add customFields prop
  currentPractice?: Practice; // Add current practice data for address display
  isCustomFieldsLoading?: boolean; // Add loading state for custom fields
  customFieldsError?: string | null; // Add error state for custom fields
  specialtyOptions?: { label: string; value: string }[]; // Add specialtyOptions
  isSpecialtyOptionsLoading?: boolean; // Add specialtyOptions loading state
  duplicateProviders?: Record<string, string>;
}

// Helper for New Patient sorting using custom field
const newPatientPriority = (provider: Provider) => {
  const value =
    provider.customFields?.[
      'New-Patient'
    ]?.[0]?.value?.toLowerCase() ?? '';
  if (value === 'priority') return 0;
  if (value === 'yes') return 1;
  if (value === 'limited') return 2;
  if (value === 'no') return 3;
  // If no value, sort last
  return 99;
};

type SortType = 'lastName' | 'onPac' | 'newPatient';

const ProvidersUnderPractice: React.FC<
  ProvidersUnderPracticeProps
> = ({
  providers = [],
  name,
  practiceId,
  customFields,
  currentPractice,
  isCustomFieldsLoading = false,
  customFieldsError = null,
  specialtyOptions = [],
  isSpecialtyOptionsLoading = false,
  duplicateProviders: duplicateProvidersProp,
}) => {
  const [sortOrder, setSortOrder] = useState<
    'ascending' | 'descending'
  >('ascending');
  const [sortType, setSortType] = useState<SortType>('lastName');
  const [viewMode, setViewMode] = useState<'list' | 'table'>('list');
  const [providerPractices, setProviderPractices] = useState<
    Record<string, Practice[]>
  >({});
  const [loadingPractices, setLoadingPractices] = useState<
    Record<string, boolean>
  >({});
  const [enrichedProviders, setEnrichedProviders] = useState<
    Provider[]
  >([]);
  const [expandAll, setExpandAll] = useState(false);
  // Use prop directly — never mutated locally, so no state copy needed.
  const duplicateProviders = useMemo(
    () => duplicateProvidersProp ?? {},
    [duplicateProvidersProp]
  );
  const isPracticeExternal = isExternalPractice(currentPractice);

  const buttonBg = useColorModeValue(
    'background.secondary',
    'gray.700'
  );
  const buttonHoverBg = useColorModeValue('gray.300', 'gray.600');

  // Memoize provider IDs and practice IDs to prevent infinite re-renders
  const providerIds = useMemo(
    () => providers.map((provider) => provider.id),
    [providers]
  );
  const practiceIds = useMemo(() => [practiceId], [practiceId]);

  // 'lastName' and 'onPac' (Status) map directly to backend sort params;
  // 'newPatient' is a custom-field-derived sort the backend has no concept
  // of, so it always stays client-side.
  const usesServerSort = sortType === 'lastName' || sortType === 'onPac';
  const [apiSortedProviders, setApiSortedProviders] = useState<
    Provider[] | null
  >(null);
  const [isApiSortLoading, setIsApiSortLoading] = useState(false);

  useEffect(() => {
    if (!usesServerSort || providerIds.length === 0) {
      setApiSortedProviders(null);
      return;
    }

    let cancelled = false;
    setIsApiSortLoading(true);

    getProvidersList(null, {
      providerIds,
      groupByStatus: sortType === 'onPac' ? 'true' : 'false',
      lastNameSort: 'true',
      sortOrderAsc: sortOrder === 'ascending' ? 'true' : 'false',
    }, providerIds.length)
      .then(({ data }) => {
        if (!cancelled) setApiSortedProviders(data?.providers ?? null);
      })
      .catch((error) => {
        console.warn('Failed to fetch sorted providers:', error);
        if (!cancelled) setApiSortedProviders(null);
      })
      .finally(() => {
        if (!cancelled) setIsApiSortLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [usesServerSort, sortType, sortOrder, providerIds]);

  // Providers actually rendered: server-sorted result when available for
  // the current sort choice, otherwise the practice's full provider list
  // (sorted client-side below).
  const baseProviders =
    usesServerSort && apiSortedProviders ? apiSortedProviders : providers;

  // Enrich provider data with full practice information for table view
  useEffect(() => {
    const enrichProviders = async () => {
      if (!baseProviders.length) {
        setEnrichedProviders([]);
        return;
      }

      const enriched = await Promise.all(
        baseProviders.map(async (provider) => {
          // Start with current practice if available. BE may already include
          // the current practice in relatedPractices, so dedupe by id to avoid
          // the multi-clinic badge tooltip listing it twice.
          const combined = currentPractice
            ? [currentPractice, ...(provider.relatedPractices || [])]
            : provider.relatedPractices || [];
          const seenIds = new Set<string>();
          const practicesToEnrich = combined.filter((p) => {
            if (!p?.id || seenIds.has(p.id)) return false;
            seenIds.add(p.id);
            return true;
          });

          if (!practicesToEnrich.length) {
            return {
              ...provider,
              relatedPractices: [],
            };
          }

          try {
            const enrichedPractices = await Promise.all(
              practicesToEnrich.map(async (practice) => {
                // If it's the current practice, use it directly (already has full data)
                if (
                  currentPractice &&
                  practice.id === currentPractice.id
                ) {
                  return currentPractice;
                }

                try {
                  const { data } = await getPracticeById(practice.id);
                  return data?.practice || practice;
                } catch (error) {
                  console.warn(
                    `Failed to fetch practice ${practice.id}:`,
                    error
                  );
                  return practice;
                }
              })
            );

            return {
              ...provider,
              relatedPractices: enrichedPractices,
            };
          } catch (error) {
            console.warn(
              `Failed to enrich provider ${provider.id}:`,
              error
            );
            return provider;
          }
        })
      );

      setEnrichedProviders(enriched);
    };

    enrichProviders();
  }, [baseProviders, currentPractice]);

  // Fetch practices for each provider to ensure OfficeNotesCard can resolve practice names
  const fetchProviderPractices = useCallback(
    async (provider: Provider) => {
      if (!provider.relatedPractices?.length) return;

      setLoadingPractices((prev) => ({
        ...prev,
        [provider.id]: true,
      }));

      try {
        const fetchedPractices = await Promise.all(
          provider.relatedPractices.map(async (practice) => {
            // Skip the current practice — already have its full data
            if (currentPractice && practice.id === currentPractice.id) {
              return currentPractice;
            }
            try {
              const { data } = await getPracticeById(practice.id);
              return data?.practice;
            } catch (error) {
              console.warn(
                `Failed to fetch practice ${practice.id}:`,
                error
              );
              return null;
            }
          })
        );

        const validPractices = fetchedPractices.filter(
          (practice): practice is Practice => !!practice
        );

        setProviderPractices((prev) => ({
          ...prev,
          [provider.id]: validPractices,
        }));
      } catch (error) {
        console.error(
          `Failed to fetch practices for provider ${provider.id}:`,
          error
        );
      } finally {
        setLoadingPractices((prev) => ({
          ...prev,
          [provider.id]: false,
        }));
      }
    },
    [currentPractice]
  );

  // Fetch practices for each provider when providers change
  useEffect(() => {
    baseProviders.forEach((provider) => {
      if (provider.relatedPractices?.length) {
        fetchProviderPractices(provider);
      }
    });
  }, [baseProviders, fetchProviderPractices]);

  // Use batch absences hook to fetch all absences in one API call
  const {
    absencesByProvider,
    loading: absencesLoading,
    error: absencesError,
    refreshAbsences,
  } = useBatchAbsences({
    providerIds,
    practiceIds,
  });

  // Enhanced sorting: User can choose sort type. When the server already
  // returned providers in the correct order (usesServerSort with a
  // resolved response), trust that order instead of re-sorting.
  const sortProviders = useCallback(
    (providerList: Provider[]) => {
      if (usesServerSort && apiSortedProviders) {
        return providerList;
      }

      return [...providerList].sort((a, b) => {
        let comparison = 0;

        switch (sortType) {
          case 'onPac': {
            // Sort by status: on PAC first, then not on PAC, then
            // terminated last.
            comparison =
              getProviderStatusRank(a) - getProviderStatusRank(b);
            break;
          }

          case 'newPatient': {
            // Sort by New Patient Priority (priority > yes > limited > no)
            const aNP = newPatientPriority(a);
            const bNP = newPatientPriority(b);
            comparison = aNP - bNP;
            break;
          }

          case 'lastName':
          default: {
            // Sort by Last Name
            const lastNameA = a.lastname?.toUpperCase() ?? '';
            const lastNameB = b.lastname?.toUpperCase() ?? '';
            comparison = lastNameA.localeCompare(lastNameB);
            break;
          }
        }

        // Apply sort order (ascending/descending)
        if (sortOrder === 'descending') {
          comparison = -comparison;
        }

        // If primary sort is equal, use last name as tie-breaker
        if (comparison === 0 && sortType !== 'lastName') {
          const lastNameA = a.lastname?.toUpperCase() ?? '';
          const lastNameB = b.lastname?.toUpperCase() ?? '';
          comparison =
            sortOrder === 'ascending'
              ? lastNameA.localeCompare(lastNameB)
              : lastNameB.localeCompare(lastNameA);
        }

        return comparison;
      });
    },
    [usesServerSort, apiSortedProviders, sortOrder, sortType]
  );

  const sortedProviders = sortProviders(baseProviders);
  const sortedEnrichedProviders = sortProviders(enrichedProviders);

  return (
    <>
      <Box ml="2rem" mr="2rem" mb="3rem" mt="1.75rem">
        <Box>
          <Flex
            justifyContent="space-between"
            alignItems="center"
            mb="1rem"
          >
            <Text
              fontSize="1.25rem"
              fontWeight="700"
              lineHeight="120%"
              color="text.secondary"
            >
              {name} Providers
            </Text>
            <Flex>
              <Select
                bg={buttonBg}
                width="12rem"
                height={'2.5rem'}
                _hover={{ bg: buttonHoverBg }}
                _active={{
                  bg: buttonHoverBg,
                  transform: 'scale(0.98)',
                }}
                borderRadius={'0.375rem'}
                border="none"
                value={sortType}
                onChange={(e) =>
                  setSortType(e.target.value as SortType)
                }
                isDisabled={isApiSortLoading}
                mr="0.75rem"
              >
                <option value="lastName">Last Name</option>
                <option value="onPac">Status</option>
                <option value="newPatient">New Patient</option>
              </Select>
              <Select
                bg={buttonBg}
                width="10.375rem"
                height={'2.5rem'}
                _hover={{ bg: buttonHoverBg }}
                _active={{
                  bg: buttonHoverBg,
                  transform: 'scale(0.98)',
                }}
                borderRadius={'0.375rem'}
                border="none"
                value={sortOrder}
                onChange={(e) =>
                  setSortOrder(
                    e.target.value as 'ascending' | 'descending'
                  )
                }
                isDisabled={isApiSortLoading}
                mr="0.75rem"
              >
                <option value="ascending">A - Z</option>
                <option value="descending">Z - A</option>
              </Select>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setExpandAll(!expandAll)}
                bg={buttonBg}
                _hover={{ bg: buttonHoverBg }}
                height="2.5rem"
                px="1rem"
                fontSize="0.875rem"
                fontWeight="500"
                borderColor="transparent"
                borderRadius="0.375rem"
                _active={{
                  transform: 'scale(0.98)',
                }}
                mr="0.75rem"
              >
                {expandAll ? 'Collapse All' : 'Expand All'}
              </Button>
              <ViewToggle
                viewMode={viewMode}
                setViewMode={setViewMode}
              />
            </Flex>
          </Flex>
          {providers.length > 0 ? (
            viewMode === 'table' ? (
              <ResourceGrid<Provider>
                data={enrichedProviders.length > 0 ? sortedEnrichedProviders : sortedProviders}
                columns={createProviderColumns(duplicateProviders)}
              />
            ) : (
              <HorizontalScrollContainer>
                {(enrichedProviders.length > 0
                  ? sortedEnrichedProviders
                  : sortedProviders
                ).map((provider, i) => {
                  // BE's relatedPractices may already include the current
                  // practice, so dedupe by id when combining with the
                  // current-practice prepend.
                  const combinedPractices = [
                    currentPractice || {
                      id: practiceId,
                      name: name || 'Current Practice',
                    },
                    ...(providerPractices[provider.id] || []),
                  ];
                  const seenPracticeIds = new Set<string>();
                  const allPractices = combinedPractices.filter((p) => {
                    if (!p?.id || seenPracticeIds.has(p.id)) return false;
                    seenPracticeIds.add(p.id);
                    return true;
                  });

                  return (
                    <ProviderCompactCard
                      provider={provider}
                      key={`${provider.id}-${i}`}
                      practices={allPractices}
                      absences={absencesByProvider[provider.id] || []}
                      loading={absencesLoading}
                      error={absencesError}
                      refreshAbsences={refreshAbsences}
                      customFields={customFields}
                      isRefreshing={
                        loadingPractices[provider.id] || false
                      }
                      isPracticesLoading={
                        loadingPractices[provider.id] || false
                      }
                      isCustomFieldsLoading={isCustomFieldsLoading}
                      customFieldsError={customFieldsError}
                      specialtyOptions={specialtyOptions}
                      isSpecialtyOptionsLoading={isSpecialtyOptionsLoading}
                      isExpanded={expandAll}
                      duplicateProviders={duplicateProviders}
                      isExternalContext={isPracticeExternal}
                    />
                  );
                })}
              </HorizontalScrollContainer>
            )
          ) : (
            <NoResourceFound
              resourceType="providers"
              showAddButton={true}
              isAdmin={true}
            />
          )}
        </Box>
      </Box>
    </>
  );
};

export default ProvidersUnderPractice;
